import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');

interface SubAdminLoginEvent {
  name: string;
  email: string;
  loginCount: number;
  timestamp: number;
}

interface ToastItem extends SubAdminLoginEvent {
  id: string;
  isExiting: boolean;
}

const slideInRight = keyframes`
  from {
    transform: translateX(120%) scale(0.85);
    opacity: 0;
  }
  70% {
    transform: translateX(-4%) scale(1.02);
    opacity: 1;
  }
  to {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
`;

const slideOutRight = keyframes`
  from {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  to {
    transform: translateX(120%) scale(0.85);
    opacity: 0;
  }
`;

const shimmerBorder = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

const Container = styled.div`
  position: fixed;
  top: 80px;
  right: 20px;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
`;

const Toast = styled.div<{ $isExiting: boolean }>`
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%);
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow:
    0 8px 32px rgba(99, 102, 241, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 290px;
  max-width: 370px;
  animation: ${props => props.$isExiting ? slideOutRight : slideInRight}
    ${props => props.$isExiting ? '0.35s' : '0.5s'}
    cubic-bezier(0.16, 1, 0.3, 1) forwards;
  pointer-events: auto;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #818cf8, #a78bfa, #c084fc, #818cf8);
    background-size: 200% auto;
    animation: ${shimmerBorder} 2s linear infinite;
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #818cf8 0%, #6366f1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  color: white;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const NameText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #e0e7ff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
`;

const StatusText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #a5b4fc;
  margin-bottom: 3px;
`;

const MetaText = styled.div`
  font-size: 10px;
  color: #6366f1;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  color: #a5b4fc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    color: #e0e7ff;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const LoginBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.2);
  padding: 1px 7px;
  border-radius: 8px;
`;

const SubAdminLoginToast: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const socketRef = useRef<Socket | null>(null);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, isExiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 350);
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL, { withCredentials: true, transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('subadmin-login', (data: SubAdminLoginEvent) => {
      const id = `sa-${data.email}-${data.timestamp}`;
      const newToast: ToastItem = { ...data, id, isExiting: false };

      setToasts(prev => [newToast, ...prev].slice(0, 3));

      // Auto-remove after 7 seconds
      setTimeout(() => removeToast(id), 7000);
    });

    return () => {
      socket.off('subadmin-login');
      socket.disconnect();
    };
  }, [removeToast]);

  if (toasts.length === 0) return null;

  return (
    <Container>
      {toasts.map(toast => (
        <Toast key={toast.id} $isExiting={toast.isExiting}>
          <Avatar>
            {toast.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
          </Avatar>
          <Content>
            <NameText title={toast.name}>{toast.name}</NameText>
            <StatusText>Sub-admin logged in</StatusText>
            <MetaText>
              <span>{toast.email}</span>
              <LoginBadge>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3"/></svg>
                #{toast.loginCount}
              </LoginBadge>
            </MetaText>
          </Content>
          <CloseBtn onClick={() => removeToast(toast.id)} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </CloseBtn>
        </Toast>
      ))}
    </Container>
  );
};

export default SubAdminLoginToast;
